import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  AlertTriangle, Clock, User, Building2, Activity, RefreshCw, 
  Calendar, MapPin, Briefcase, LogOut, Edit, ChevronDown, X
} from 'lucide-react';
import { formatUtcTimeOnly } from '../../../../../utils/timezoneUtils';

const LongShiftsTab = ({ setError, setSuccess }) => {
  const [longShifts, setLongShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [clockingOut, setClockingOut] = useState({});
  const [selectedHours, setSelectedHours] = useState('10+');
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  const scrollContainerRef = useRef(null);
  const token = localStorage.getItem('token');

  // Hours options for the dropdown
  const hoursOptions = [
    { value: '4', label: '4+ Hours' },
    { value: '5', label: '5+ Hours' },
    { value: '6', label: '6+ Hours' },
    { value: '7', label: '7+ Hours' },
    { value: '8', label: '8+ Hours' },
    { value: '9', label: '9+ Hours' },
    { value: '10+', label: '10+ Hours' }
  ];

  const fetchLongShifts = useCallback(async (isRefresh = false, loadMore = false) => {
    if (!token) {
      setError('No authentication token available');
      setLoading(false);
      return;
    }

    try {
      if (isRefresh) {
        setRefreshing(true);
        setCurrentPage(0);
        setHasMore(true);
      } else if (loadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setCurrentPage(0);
        setHasMore(true);
      }
      
      setError(null);
      
      const page = loadMore ? currentPage + 1 : 0;
      const queryParams = new URLSearchParams({
        hours: selectedHours,
        offset: (page * 250).toString(),
        limit: '250'
      });
      
      const response = await fetch(`/api/labor/long-shifts?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch long shifts: ${response.status}`);
      }

      const data = await response.json();
      const newShifts = data.longShifts || [];
      
      if (loadMore) {
        setLongShifts(prevShifts => [...prevShifts, ...newShifts]);
        setCurrentPage(page);
      } else {
        setLongShifts(newShifts);
        setCurrentPage(0);
      }
      
      // Check if there are more records to load
      setHasMore(newShifts.length === 250);
      
    } catch (error) {
      console.error('Error fetching long shifts:', error);
      setError(error.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingMore(false);
    }
  }, [token, selectedHours, currentPage]);

  useEffect(() => {
    fetchLongShifts();
  }, [fetchLongShifts]);

  // Auto-refresh every 60 seconds to keep active shifts updated
  useEffect(() => {
    const interval = setInterval(() => {
      if (longShifts.some(shift => !shift.clock_out_time)) {
        fetchLongShifts(true);
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [longShifts, fetchLongShifts]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || loadingMore || !hasMore) return;
    
    const container = scrollContainerRef.current;
    const { scrollTop, scrollHeight, clientHeight } = container;
    
    console.log('Scroll event:', { scrollTop, scrollHeight, clientHeight, loadingMore, hasMore, currentPage });
    
    // Load more when user scrolls within 100px of the bottom
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      console.log('Triggering load more...');
      fetchLongShifts(false, true);
    }
  }, [loadingMore, hasMore, currentPage, fetchLongShifts]);

  // Add scroll event listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Hours filter handler
  const handleHoursChange = (newHours) => {
    setSelectedHours(newHours);
    setLongShifts([]);
    setCurrentPage(0);
    setHasMore(true);
  };

  // Edit handlers
  const handleEditClick = (shift) => {
    setSelectedEntry(shift);
    setShowEditModal(true);
  };

  // Helper functions for UTC/local timezone conversion
  const convertToUTC = (localDateTime) => {
    if (!localDateTime) return null;
    return new Date(localDateTime).toISOString();
  };

  const convertFromUTC = (utcDateTime) => {
    if (!utcDateTime) return '';
    const date = new Date(utcDateTime);
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - (offset * 60 * 1000));
    return localDate.toISOString().slice(0, 16);
  };

  // Handle edit save
  const handleSaveEdit = async (formData) => {
    try {
      const response = await fetch(`/api/labor/${selectedEntry.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error response:', errorText);
        throw new Error(`Server error: ${response.status}`);
      }
      
      const updatedData = await response.json();
      
      // Update the shift in our local state
      setLongShifts(shifts => 
        shifts.map(shift => 
          shift.id === updatedData.entry.id ? updatedData.entry : shift
        )
      );
      
      setShowEditModal(false);
      setSuccess("Entry updated successfully!");
    } catch (error) {
      console.error('Error updating entry:', error);
      setError(`Error updating entry: ${error.message}`);
    }
  };

  const handleClockOut = async (shift) => {
    const confirmed = window.confirm(
      `Are you sure you want to clock out ${shift.full_name}?\n\nThey have been working for ${formatDuration(shift.seconds_worked / 3600)}.`
    );
    
    if (!confirmed) return;

    if (!token) {
      setError('No authentication token available');
      return;
    }

    try {
      setClockingOut(prev => ({ ...prev, [shift.id]: true }));
      setError(null);

      const response = await fetch(`/api/labor/${shift.id}/clockout`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to clock out user: ${response.status}`);
      }

      setSuccess(`Successfully clocked out ${shift.full_name}`);
      
      // Refresh the long shifts list to remove the clocked out user
      await fetchLongShifts(true);
    } catch (error) {
      console.error('Error clocking out user:', error);
      setError(`Failed to clock out ${shift.full_name}: ${error.message}`);
    } finally {
      setClockingOut(prev => ({ ...prev, [shift.id]: false }));
    }
  };

  const getHoursWorked = (clockInTime) => {
    if (!clockInTime) return 0;
    
    const startDate = new Date(clockInTime);
    const now = new Date();
    const diff = now.getTime() - startDate.getTime();
    return diff / (1000 * 60 * 60); // Convert to hours
  };

  const formatDuration = (hours) => {
    const wholeHours = Math.floor(hours);
    const minutes = Math.floor((hours - wholeHours) * 60);
    return `${wholeHours}h ${minutes}m`;
  };

  const getSeverityColor = (hours) => {
    if (hours >= 16) return 'border-red-600 bg-red-900/20';
    if (hours >= 14) return 'border-orange-600 bg-orange-900/20';
    if (hours >= 12) return 'border-yellow-600 bg-yellow-900/20';
    return 'border-blue-600 bg-blue-900/20';
  };

  const getSeverityIcon = (hours) => {
    if (hours >= 16) return <AlertTriangle className="w-5 h-5 text-red-400" />;
    if (hours >= 14) return <AlertTriangle className="w-5 h-5 text-orange-400" />;
    if (hours >= 12) return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
    return <Clock className="w-5 h-5 text-blue-400" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Loading long shifts...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            <h3 className="text-lg font-medium text-slate-200">
              Long Shifts ({selectedHours === '10+' ? '10+ Hours' : `${selectedHours}+ Hours`})
            </h3>
            <span className="px-2 py-1 bg-slate-700 text-slate-300 text-xs rounded-full">
              {longShifts.length} {loadingMore && 'of '}{longShifts.length === 1 ? 'entry' : 'entries'}
            </span>
          </div>
          
          {/* Hours Filter Dropdown */}
          <div className="relative">
            <select
              value={selectedHours}
              onChange={(e) => handleHoursChange(e.target.value)}
              className="appearance-none bg-slate-700 text-slate-200 border border-slate-600 rounded-md px-3 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {hoursOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </div>
        
        <button
          onClick={() => fetchLongShifts(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-md transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Alert Info */}
      <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-yellow-200 mb-1">Long Shift Alert</h4>
            <p className="text-sm text-yellow-300/80">
              Users shown below have been clocked in for more than {selectedHours === '10+' ? '10' : selectedHours} hours. 
              Use the edit button to modify entries or the clock-out button for active shifts. 
              Scroll down to load more entries automatically.
            </p>
          </div>
        </div>
      </div>

      {/* Long Shifts List */}
      {longShifts.length === 0 && !loading ? (
        <div className="text-center py-12">
          <Clock className="w-12 h-12 text-slate-500 mx-auto mb-3" />
          <p className="text-slate-400">No long shifts detected</p>
          <p className="text-sm text-slate-500 mt-1">
            All users are within {selectedHours === '10+' ? '10' : selectedHours} hour limit
          </p>
        </div>
      ) : (
        <div 
          ref={scrollContainerRef}
          className="overflow-y-auto"
          style={{ 
            height: 'calc(100vh - 300px)',
            minHeight: '400px',
            maxHeight: 'calc(100vh - 200px)'
          }}
        >
          <div className="space-y-1 p-1">
            {longShifts.map((shift) => {
            const hoursWorked = shift.seconds_worked / 3600;
            return (
              <div
                key={shift.id}
                className={`border rounded-lg px-3 py-1 ${getSeverityColor(hoursWorked)}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1">
                    {getSeverityIcon(hoursWorked)}
                    <span className="font-medium text-slate-200">
                      {shift.full_name} ({shift.employee_id})
                    </span>
                    <span className="text-slate-400 text-sm">•</span>
                    <span className="text-slate-400 text-sm">
                      {formatUtcTimeOnly(shift.clock_in_time, 'America/Chicago').replace(/\s[A-Z]{2,4}$/, '')}
                    </span>
                    <span className="text-slate-400 text-sm">•</span>
                    <span className="text-slate-400 text-sm">
                      {shift.activity || 'N/A'}
                    </span>
                    <span className="text-slate-400 text-sm">•</span>
                    <span className="text-slate-400 text-sm">
                      {shift.business_unit || 'N/A'}
                    </span>
                    {shift.project && (
                      <>
                        <span className="text-slate-400 text-sm">•</span>
                        <span className="text-slate-400 text-sm">
                          {shift.project}
                        </span>
                      </>
                    )}
                    {shift.location && (
                      <>
                        <span className="text-slate-400 text-sm">•</span>
                        <span className="text-slate-400 text-sm">
                          {shift.location}
                        </span>
                      </>
                    )}
                    {shift.notes && (
                      <>
                        <span className="text-slate-400 text-sm">•</span>
                        <span className="text-slate-400 text-sm italic">
                          {shift.notes.length > 50 ? `${shift.notes.substring(0, 50)}...` : shift.notes}
                        </span>
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-lg font-bold text-slate-200">
                      {formatDuration(hoursWorked)}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditClick(shift)}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
                      >
                        <Edit className="w-3 h-3" />
                        Edit
                      </button>

                      {!shift.clock_out_time && (
                        <button
                          onClick={() => handleClockOut(shift)}
                          disabled={clockingOut[shift.id]}
                          className="flex items-center gap-1 px-2 py-1 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:opacity-50 text-white text-xs rounded transition-colors"
                        >
                          {clockingOut[shift.id] ? (
                            <>
                              <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                              </svg>
                              Clocking Out...
                            </>
                          ) : (
                            <>
                              <LogOut className="w-3 h-3" />
                              Clock Out
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
            
            {/* Loading More Indicator */}
            {loadingMore && (
              <div className="flex items-center justify-center py-6">
                <div className="flex items-center gap-2 text-slate-400">
                  <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  Loading more entries...
                </div>
              </div>
            )}
            
            {/* End of results indicator */}
            {!hasMore && longShifts.length > 0 && (
              <div className="text-center py-4 text-slate-400 text-sm">
                All entries loaded ({longShifts.length} total)
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Edit Modal */}
      {showEditModal && selectedEntry && (
        <EditEntryModal 
          entry={selectedEntry} 
          onSave={handleSaveEdit}
          onCancel={() => setShowEditModal(false)}
          convertFromUTC={convertFromUTC}
          convertToUTC={convertToUTC}
        />
      )}
    </div>
  );
};

// Edit Entry Modal Component
const EditEntryModal = ({ entry, onSave, onCancel, convertFromUTC, convertToUTC }) => {
  const [formData, setFormData] = useState({
    activity: entry.activity || '',
    business_unit: entry.business_unit || '',
    group: entry.group || '',
    project: entry.project || '',
    shift: entry.shift || '',
    team: entry.team || '',
    location: entry.location || '',
    notes: entry.notes || '',
    reference1: entry.reference1 || '',
    reference2: entry.reference2 || '',
    reference3: entry.reference3 || '',
    // Format date and time for input elements (convert from UTC to local timezone)
    date: entry.date || '',
    clock_in_time: convertFromUTC(entry.clock_in_time),
    clock_out_time: convertFromUTC(entry.clock_out_time),
  });

  const [activities, setActivities] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [teams, setTeams] = useState([]);
  const [workGroups, setWorkGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch all dropdown options when modal opens
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      setLoading(true);
      try {
        // Fetch activities
        const activitiesResponse = await fetch('/api/activities', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (activitiesResponse.ok) {
          const activitiesData = await activitiesResponse.json();
          setActivities(activitiesData);
        }
        
        // Fetch business units
        const businessUnitsResponse = await fetch('/api/business-units', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (businessUnitsResponse.ok) {
          const businessUnitsData = await businessUnitsResponse.json();
          setBusinessUnits(businessUnitsData);
        }
        
        // Fetch projects
        const projectsResponse = await fetch('/api/projects', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (projectsResponse.ok) {
          const projectsData = await projectsResponse.json();
          setProjects(projectsData);
        }
        
        // Fetch shifts
        const shiftsResponse = await fetch('/api/shifts', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (shiftsResponse.ok) {
          const shiftsData = await shiftsResponse.json();
          setShifts(shiftsData);
        }
        
        // Fetch teams
        const teamsResponse = await fetch('/api/teams', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (teamsResponse.ok) {
          const teamsData = await teamsResponse.json();
          setTeams(teamsData);
        }
        
        // Fetch work groups
        const workGroupsResponse = await fetch('/api/work-groups', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (workGroupsResponse.ok) {
          const workGroupsData = await workGroupsResponse.json();
          setWorkGroups(workGroupsData);
        }
      } catch (error) {
        console.error("Error fetching dropdown options:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDropdownOptions();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Convert datetime fields to UTC before sending to server
    const dataToSend = {
      ...formData,
      clock_in_time: formData.clock_in_time ? convertToUTC(formData.clock_in_time) : null,
      clock_out_time: formData.clock_out_time ? convertToUTC(formData.clock_out_time) : null,
    };
    
    onSave(dataToSend);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-semibold text-slate-200 mb-4">Edit Entry #{entry.id}</h3>
        
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Read-only fields */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">ID</label>
              <input 
                type="text" 
                value={entry.id} 
                disabled
                className="bg-slate-700 text-slate-300 border border-slate-600 rounded w-full py-2 px-3 disabled:opacity-70"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Employee ID</label>
              <input 
                type="text" 
                value={entry.employee_id} 
                disabled
                className="bg-slate-700 text-slate-300 border border-slate-600 rounded w-full py-2 px-3 disabled:opacity-70"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Full Name</label>
              <input 
                type="text" 
                value={entry.full_name} 
                disabled
                className="bg-slate-700 text-slate-300 border border-slate-600 rounded w-full py-2 px-3 disabled:opacity-70"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Total Time</label>
              <input 
                type="text" 
                value={entry.total_time || 'N/A'} 
                disabled
                className="bg-slate-700 text-slate-300 border border-slate-600 rounded w-full py-2 px-3 disabled:opacity-70"
              />
            </div>
            
            {/* Editable fields */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Activity</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="activity"
                  value={formData.activity}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select an activity</option>
                  {activities.filter(activity => activity.is_active).map(activity => (
                    <option key={activity.id} value={activity.name}>
                      {activity.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Business Unit</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="business_unit"
                  value={formData.business_unit}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a business unit</option>
                  {businessUnits.filter(unit => unit.is_active).map(unit => (
                    <option key={unit.id} value={unit.identifier}>
                      {unit.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Group</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="group"
                  value={formData.group || ""}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a group</option>
                  {workGroups.filter(group => group.is_active).map(group => (
                    <option key={group.id} value={group.name}>
                      {group.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Project</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="project"
                  value={formData.project || ""}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a project</option>
                  {projects.filter(project => project.is_active).map(project => (
                    <option key={project.id} value={project.name}>
                      {project.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Shift</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="shift"
                  value={formData.shift || ""}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a shift</option>
                  {shifts.map(shift => (
                    <option key={shift.id} value={shift.name}>
                      {shift.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Team</label>
              {loading ? (
                <div className="bg-slate-700 text-slate-400 border border-slate-600 rounded w-full py-2 px-3">
                  Loading options...
                </div>
              ) : (
                <select
                  name="team"
                  value={formData.team || ""}
                  onChange={handleChange}
                  className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a team</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.name}>
                      {team.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Location</label>
              <input 
                type="text" 
                name="location"
                value={formData.location} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Date</label>
              <input 
                type="date" 
                name="date"
                value={formData.date} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Clock In Time</label>
              <input 
                type="datetime-local" 
                name="clock_in_time"
                value={formData.clock_in_time} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Clock Out Time</label>
              <input 
                type="datetime-local" 
                name="clock_out_time"
                value={formData.clock_out_time} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Reference 1</label>
              <input 
                type="text" 
                name="reference1"
                value={formData.reference1} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Reference 2</label>
              <input 
                type="text" 
                name="reference2"
                value={formData.reference2} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-slate-400 mb-1">Reference 3</label>
              <input 
                type="text" 
                name="reference3"
                value={formData.reference3} 
                onChange={handleChange}
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-400 mb-1">Notes</label>
              <textarea 
                name="notes"
                value={formData.notes} 
                onChange={handleChange}
                rows="3"
                className="bg-slate-700 text-slate-200 border border-slate-600 rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              ></textarea>
            </div>
          </div>
          
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onCancel}
              className="bg-slate-700 text-slate-300 px-4 py-2 rounded hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LongShiftsTab;